import { ContextType } from 'react';
import moment from 'moment';
import {
  formatArea,
  formatDistance,
  formatDurationCompact,
  formatVolume,
  round,
  sortAlphaBeta,
  sortByPosition,
} from '.';
import { USER_CONSTANTS } from '../constants';
import {
  ARTICLE_STATUS,
  ARTICLE_UNIT_TYPES,
  ArticleUnitType,
  PHYTO_VIRTUAL_ARTICLE_ID,
} from '../constants/articleConstants';
import { TASK_STATUS, TaskStatus } from '../constants/taskConstants';
import { UIContext } from '../contexts/UIProvider';
import { I18n, LocaleKey } from '../i18n';
import { Article } from '../slices/articles';
import { ClusterId } from '../slices/clusters';
import { Equipment } from '../slices/equipments';

export function getArticleUnitTypes(userUnits: ContextType<typeof UIContext>['units']) {
  return [
    {
      id: ARTICLE_UNIT_TYPES.AREA,
      label: I18n.t(`ArticleUnits.${ARTICLE_UNIT_TYPES.AREA}_${userUnits.area}`),
      value: userUnits.area === USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI ? 'ha' : 'ac',
    },
    {
      id: ARTICLE_UNIT_TYPES.TIME,
      label: I18n.t(`ArticleUnits.${ARTICLE_UNIT_TYPES.TIME}`),
      value: 'H',
    },
    {
      id: ARTICLE_UNIT_TYPES.DISTANCE,
      label: I18n.t(`ArticleUnits.${ARTICLE_UNIT_TYPES.DISTANCE}_${userUnits.distance}`),
      value: userUnits.area === USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI ? 'km' : 'mi',
    },
    {
      id: ARTICLE_UNIT_TYPES.UNIT,
      label: I18n.t(`ArticleUnits.${ARTICLE_UNIT_TYPES.UNIT}`),
      value: 'unit',
    },
    {
      id: ARTICLE_UNIT_TYPES.PACKAGE,
      label: I18n.t(`ArticleUnits.${ARTICLE_UNIT_TYPES.PACKAGE}`),
      value: 'forfait',
    },
    {
      id: ARTICLE_UNIT_TYPES.MASS,
      label: I18n.t(`ArticleUnits.${ARTICLE_UNIT_TYPES.MASS}`),
      value: 'tonnes',
    },
    {
      id: ARTICLE_UNIT_TYPES.VOLUME,
      label: I18n.t(`ArticleUnits.${ARTICLE_UNIT_TYPES.VOLUME}`),
      value: 'L',
    },
    {
      id: ARTICLE_UNIT_TYPES.OTHER,
      label: I18n.t(`ArticleUnits.${ARTICLE_UNIT_TYPES.OTHER}`),
      value: 'other',
    },
  ] as const;
}

export function getArticleUnitLabel(unit: string, userUnits: ContextType<typeof UIContext>['units']) {
  const unitType = getUnitTypeFromUnit(unit);
  if (!unitType) return unit;
  const key = `ArticleUnits.${unitType}` as const;
  switch (unitType) {
    case ARTICLE_UNIT_TYPES.AREA:
      return I18n.t(`${key}_${userUnits.area}` as LocaleKey).toLocaleLowerCase();
    case ARTICLE_UNIT_TYPES.DISTANCE:
      return I18n.t(`${key}_${userUnits.distance}` as LocaleKey).toLocaleLowerCase();
    case ARTICLE_UNIT_TYPES.VOLUME:
      return I18n.t(key as LocaleKey);
    default:
      return I18n.t(key as LocaleKey).toLocaleLowerCase();
  }
}

export function getUnitTypeFromUnit(unit: string) {
  switch (unit) {
    case 'ha':
    case 'ac':
      return ARTICLE_UNIT_TYPES.AREA;
    case 'H':
      return ARTICLE_UNIT_TYPES.TIME;
    case 'km':
    case 'mi':
      return ARTICLE_UNIT_TYPES.DISTANCE;
    case 'unit':
      return ARTICLE_UNIT_TYPES.UNIT;
    case 'forfait':
      return ARTICLE_UNIT_TYPES.PACKAGE;
    case 'tonnes':
      return ARTICLE_UNIT_TYPES.MASS;
    case 'L':
      return ARTICLE_UNIT_TYPES.VOLUME;
    default:
      return null;
  }
}

function getUnitTypeFromMetric(metric?: string) {
  if (metric?.includes('area')) {
    return ARTICLE_UNIT_TYPES.AREA;
  } else if (metric?.includes('duration')) {
    return ARTICLE_UNIT_TYPES.TIME;
  } else if (metric?.includes('distance')) {
    return ARTICLE_UNIT_TYPES.DISTANCE;
  } else if (metric?.includes('unit')) {
    return ARTICLE_UNIT_TYPES.UNIT;
  } else if (metric?.includes('fuel')) {
    return ARTICLE_UNIT_TYPES.VOLUME;
  }
  return null;
}

export function getDefaultMetricForUnitType(unit: ArticleUnitType) {
  switch (unit) {
    case ARTICLE_UNIT_TYPES.AREA:
      return 'total_area';
    case ARTICLE_UNIT_TYPES.TIME:
      return 'total_motor_duration';
    case ARTICLE_UNIT_TYPES.DISTANCE:
      return 'total_distance';
    case ARTICLE_UNIT_TYPES.UNIT:
      return 'total_unit';
    case ARTICLE_UNIT_TYPES.VOLUME:
      return 'total_fuel';
    default:
      return null;
  }
}

export function getArticleMetricForKey(key: string) {
  return { id: key, label: I18n.t(`Metrics.${key}` as LocaleKey) };
}

function formatEmpty(quantity: number | null, formatter: () => string) {
  return quantity === null ? '' : formatter();
}

export function getQuantityFormatter(
  article: Article | undefined,
  userUnits: ContextType<typeof UIContext>['units'],
  withUnit = false,
): (quantity: number | null) => string {
  if (!article) {
    return () => '';
  }
  if (article.id === PHYTO_VIRTUAL_ARTICLE_ID) {
    return (q) => formatEmpty(q, () => formatArea(q!, userUnits, withUnit, true));
  }
  return (q) =>
    formatEmpty(q, () => `${round(q)}${withUnit ? ` ${getArticleUnitLabel(article.unit, userUnits)}` : ''}`);
}

export function getComputedQuantityFormatter(
  article: Article | undefined,
  userUnits: ContextType<typeof UIContext>['units'],
  withUnit = false,
): (quantity: number | null) => string {
  switch (getUnitTypeFromMetric(article?.karnott_metric)) {
    case ARTICLE_UNIT_TYPES.TIME:
      return (q) => formatEmpty(q, () => formatDurationCompact(q!, 2, withUnit));
    case ARTICLE_UNIT_TYPES.DISTANCE:
      return (q) => formatEmpty(q, () => formatDistance(q!, userUnits, withUnit, true));
    case ARTICLE_UNIT_TYPES.UNIT:
      return (q) => formatEmpty(q, () => `${q}${withUnit ? ` ${I18n.t('MetricUnit.unit', q!)}` : ''}`);
    case ARTICLE_UNIT_TYPES.VOLUME:
      return (q) => formatEmpty(q, () => formatVolume(q!, userUnits, withUnit, true));
    default:
      // if article has no metric specified, the default value is the total_area
      return (q) => formatEmpty(q, () => formatArea(q!, userUnits, withUnit, true));
  }
}

export function isArticleArchived(article: Article) {
  return article.status === ARTICLE_STATUS.ARCHIVED;
}

export function getVirtualPhytoArticle({
  clusterId,
  userUnits,
  taskStatus,
}: {
  clusterId?: ClusterId;
  userUnits?: ContextType<typeof UIContext>['units'];
  taskStatus?: TaskStatus;
}): Article & { equipment_instances: Equipment[] } {
  return {
    id: PHYTO_VIRTUAL_ARTICLE_ID,
    authorizations: { can_update: false, can_delete: false, can_archive: false },
    cluster_id: clusterId ?? 0,
    name: taskStatus === TASK_STATUS.TO_DO ? I18n.t('Tasks.column_area_todo') : I18n.t('Tasks.column_area'),
    unit: userUnits?.area === USER_CONSTANTS.UNITS.DISTANCE_AND_AREA.SI ? 'ha' : 'ac',
    status: ARTICLE_STATUS.AVAILABLE,
    karnott_metric: 'total_area',
    typology: '',
    equipment_instances: [],
    is_generated: true,
  };
}

/** Check whether the unit of the article is coherent with the unit of the selected metric */
function areUnitsCoherent(article?: Article) {
  if (!article) return false;
  const articleUnitType = getUnitTypeFromUnit(article.unit);
  const metricUnitType = getUnitTypeFromMetric(article.karnott_metric);
  return articleUnitType === metricUnitType;
}

/** Returns a coherent computed value with the article's unit, or null if the metric and the unit are not reconcilable */
export function getComputedQuantityToFill(
  article: Article | undefined,
  quantity: number | null,
  units: ContextType<typeof UIContext>['units'],
) {
  const formatter = getComputedQuantityFormatter(article, units, false);
  if (!article || !quantity) return null;
  if (areUnitsCoherent(article)) {
    return Number(formatter(quantity));
  } else {
    const articleUnitType = getUnitTypeFromUnit(article.unit);
    const formattedUnitTypes: (string | null)[] = [
      ARTICLE_UNIT_TYPES.AREA,
      ARTICLE_UNIT_TYPES.TIME,
      ARTICLE_UNIT_TYPES.DISTANCE,
    ];
    if (formattedUnitTypes.includes(articleUnitType)) {
      // if the units are incoherent and the value needs to be formatted, the formatting would not make sense
      return null;
    }
    return Number(formatter(quantity));
  }
}

export function sortTaskArticles(articleA: Article, articleB: Article) {
  if (articleA.id === PHYTO_VIRTUAL_ARTICLE_ID && articleB.id !== PHYTO_VIRTUAL_ARTICLE_ID) {
    return -1;
  } else if (articleA.id !== PHYTO_VIRTUAL_ARTICLE_ID && articleB.id === PHYTO_VIRTUAL_ARTICLE_ID) {
    return 1;
  }
  if (articleA.position != undefined && articleB.position != undefined) {
    return sortByPosition(articleA, articleB);
  }
  if (articleA.position != undefined) {
    return -1;
  }
  if (articleB.position != undefined) {
    return 1;
  }
  return sortAlphaBeta(articleA.name, articleB.name);
}

export function getArticleRangeValue(
  article: Article & {
    ranges?: Record<
      ClusterId,
      (
        | {
            range_type: 'float';
            from?: number;
            to?: number;
          }
        | {
            range_type: 'datetime';
            from?: string;
            to?: string;
          }
      )[]
    >;
  },
  clientId: ClusterId,
): number | null {
  const articleRanges = article.ranges?.[clientId];
  if (!articleRanges || articleRanges.length === 0) {
    return null;
  }
  let total = 0;
  for (const range of articleRanges.filter((r) => r.from != null && r.to != null)) {
    if (range.range_type === 'float') {
      const from = range.from ?? 0;
      const to = range.to ?? 0;
      total += to - from;
    } else {
      const fromDate = range.from != null ? moment(range.from) : null;
      const toDate = range.to != null ? moment(range.to) : null;
      if (fromDate != null && toDate != null) {
        total += toDate.diff(fromDate, 'hours', true);
      }
    }
  }
  return total;
}
